import { getMe } from 'api/auth';
import { fetchLegacyPrizesForValue, fetchPrizes } from 'api/prizes';
import CreateAccountPopup, {
  Prompt,
} from 'components/account/CreateAccountPopup';
import PrizeFilterSelector from 'components/landing/PrizeFilterSelector';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PageTitle from 'components/PageTitle';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import ChoosePrizeModal from 'components/prizes/ChoosePrizeModal';
import { getPrizePageDetails } from 'components/prizes/prize-page/PrizesPageDetails';
import PrizeCardWithTitleBelow from 'components/prizes/PrizeCardWithTitleBelow';
import PrizeList from 'components/prizes/PrizeList';
import { PrizePageNav } from 'components/prizes/PrizePageNav';
import PrizeValueDropdownSelector from 'components/prizes/PrizeValueDropdownSelector/PrizeValueDropdownSelector';
import { RedemptionFlow } from 'components/prizes/redemption-flow/RedemptionFlow';
import SubcategorySelector from 'components/prizes/SubcategorySelector/SubcategorySelector';
import Separator from 'components/Separator';
import Subheading from 'components/Subheading';
import SubmitPrizeSection from 'components/SubmitPrizeSection';
import TestimonialAdSection from 'components/TestimonialAdSection';
import ToggleButton from 'components/ToggleButton';
import IntroducingWishListModal from 'components/wish-list/IntroducingWishListModal';
import {
  getPrizeTierByName,
  LottoPrizeValueFilterOptions,
} from 'constants/LottoPrizeValues';
import { useFetchPrizes } from 'hooks/prizes/useFetchPrizes';
import { useGuestDetails } from 'hooks/prizes/useGuestDetails';
import { useValidationResult } from 'hooks/prizes/useValidationResult';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { scrollPositionExists, useScrollRestoration } from 'hooks/useScrollRestoration';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import Router, { useRouter } from 'next/router';
import PrizeFilterOptions from 'prizes/weekly-draw/PrizeFilterOptions';
import { useEffect, useRef, useState } from 'react';
import { Prize } from 'types/Prize';
import {
  EecProduct,
  trackEecImpression,
  trackEecProductClick,
} from 'utils/analytics/analytics';
import { getSubscriberStatus, sleep } from 'utils/common';
import { ASSET_PREFIX, colors, Cta, SubscriberStatus } from 'utils/constants';
import { getCookie } from 'utils/cookie';
import { SUGGEST_PRIZE_ID } from 'constants/FaqConstants';


const EXPAND_LIMIT = 17;

const PrizesPage = observer(
  ({
    tokenRedemptionOption,
    tier,
    legacyValue,
    pwid,
    emailAddress,
    prizes,
  }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    // Stores
    const { currentUser, setCurrentUser } = useUserStore();
    const { displayToast, navigateToAccount } = useUIStore();
    const {
      prizesWonCount,
      treesPlantedCount,
      weeklyDrawGroupedPrizeCategories,
    } = useDrawStore();
    // Router settings
    const router = useRouter();
    const scrollPosition = scrollPositionExists(router)
    useScrollRestoration(router);
    // Page UI settings
    const [expanded, setExpanded] = useState(scrollPosition ? true : false);
    const [prizesAreGroupedByPageSection, setPrizesAreGroupedByPageSection] =
      useState(false);

    const { validationResult } = useValidationResult(
      setPrizesAreGroupedByPageSection,
      pwid
    );

    const { prizesToSelect } = useFetchPrizes(
      setPrizesAreGroupedByPageSection,
      tokenRedemptionOption,
      tier,
      legacyValue,
      pwid,
      validationResult
    );

    const { guestDetails } = useGuestDetails(pwid, emailAddress);

    const choosingPrize =
      tokenRedemptionOption ||
      (validationResult?.valid === true && validationResult.prizeWinId);

    const [selectedPrize, setSelectedPrize] = useState<any>(null);
    const inGroupedRedemptionFlow =
      choosingPrize && prizesAreGroupedByPageSection;

    const tierQueryString = (router?.query?.tier as string) ?? undefined;
    const tierValue = getPrizeTierByName(tierQueryString)?.value ?? null;
    let prizeValueFilter = null; // Only used when redeeming tokens for £5 and £10 prizes.

    if (
      tokenRedemptionOption &&
      !tierValue &&
      ['5', '10'].includes(router?.query?.value as string)
    ) {
      prizeValueFilter = parseInt(router.query.value as string);
    }
    // `null` indicates no filter
    const [valueFilter, setValueFilter] = useState<number | null>(
      tierValue ?? prizeValueFilter
    );

    // Main categories e.g. trips-and-stays, family, experiences.
    const filterId = router.query?.filter;

    // When a `filterId` is supplied (i.e. we are on a specific prize page such as Trips & Stays), then `selectedSubcategory`
    // will also be considered when filtering the visible prizes. `null` indicates no filter. The available options for
    // selectedSubcategory are available in WeeklyDrawPrizeSubcategoryDisplayNames.js.
    const [selectedSubcategory, setSelectedSubcategory] = useState<
      null | string
    >((router.query?.subcategory as string) ?? null);
    const getProductList = () => `prizes-page:${filterId ?? 'none'}`;

    const [loadingMorePrizes, setLoadingMorePrizes] = useState(false);

    // Only shown if guest player tries to redeem a prize.
    const [createAccountPopupAccountInfo, setCreateAccountPopupAccountInfo] =
      useState<{
        userId: string;
        emailAddress: string;
        wonByGuest: string;
      } | null>(null);

    const [showIntroducingWishListModal, setShowIntroducingWishListModal] =
      useState(false);

    const trackProductClickAndGoToEnter = async () => {
      const cta = currentUser?.subscriberStatus
        ? Cta[currentUser.subscriberStatus]
        : Cta.NON_SUBSCRIBER;
      if (!cta) return;
      if (cta.url === '/enter') {
        // Track product click with GA EEC if it's a regular Enter Now button.
        await trackEecProductClick(getProductList(), EecProduct.LOTTERY);
      }
      router.push(cta.url);
    };

    const {
      title,
      description,
      availabilityNotice,
      allowedCategories,
      subcategories,
      metaTitle,
      metaDescription,
      bottomInfoAndFaqSection,
    } = getPrizePageDetails(
      filterId,
      choosingPrize,
      prizesWonCount,
      treesPlantedCount,
      trackProductClickAndGoToEnter,
      validationResult,
      weeklyDrawGroupedPrizeCategories
    );

    let shouldShowExpandButton = false;

    // Used if showing one long list of prizes with no sections (the old page style).
    const filteredPrizes =
      inGroupedRedemptionFlow || !prizes || !Array.isArray(prizes)
        ? []
        : prizes.reduce((filteredPrizes, prize) => {
            // Use reduce instead of filter, so we can dynamically check the size of `filteredPrizes` in the body below.
            // Note: we don't collapse/expand if redeeming prize and prizes are grouped by section
            if (
              !expanded &&
              filteredPrizes.length > EXPAND_LIMIT &&
              !inGroupedRedemptionFlow
            ) {
              shouldShowExpandButton = true;
              return filteredPrizes;
            }

            if (allowedCategories) {
              let allowedCategoriesOrSubcategory = allowedCategories;
              if (selectedSubcategory) {
                // When a subcategory is selected, then that is the only allowed category.
                allowedCategoriesOrSubcategory = [selectedSubcategory];
              }
              let allowed = false;
              for (let i = 0; i < allowedCategoriesOrSubcategory.length; i++) {
                const category = allowedCategoriesOrSubcategory[i];
                if (prize?.categories?.includes(category)) {
                  allowed = true;
                  break;
                }
              }
              if (!allowed) return filteredPrizes;
            }

            if (valueFilter && valueFilter !== prize.value) {
              return filteredPrizes;
            }

            filteredPrizes.push(prize);

            return filteredPrizes;
          }, []);

    // These variables are passed to the PrizePageNav breadcrumb component (if a filter is applied).
    let prizePageNavPrizeCategoryName, prizePageNavPrizeCategoryHref;
    if (filterId) {
      const prizeFilterOption = PrizeFilterOptions.find(
        (option) => option.id === filterId
      );
      if (prizeFilterOption) {
        prizePageNavPrizeCategoryName = prizeFilterOption.name;
        prizePageNavPrizeCategoryHref = prizeFilterOption.href;
      }
    }

    useEffect(() => {
      const abortController = new AbortController();

      setSelectedSubcategory(null);
      trackEecImpression(getProductList(), EecProduct.LOTTERY);
      return () => {
        abortController.abort(); // This would abort the trackEecImpression operation
      };
    }, [filterId]);

    useEffect(() => {
      const abortController = new AbortController();

      if (validationResult) {
        if (validationResult.valid === false) {
          displayToast({
            title: validationResult.reason,
            color: '#FF6600',
            timeout: 5000,
          });

          if (validationResult.reason === 'Prize has already been chosen') {
            router.push('/account/my-prizes');
          }
        } else if (
          validationResult.valid === true &&
          validationResult.prizeCode
        ) {
          setExpanded(true);
        }
      }
      return () => {
        abortController.abort(); // This would abort the trackEecImpression operation
      };
    }, [validationResult]);

    const shouldShowWishListIntroductionModal = () => {
      // Only show to logged in users.
      if (!currentUser) return false;

      // Only show to a user once.
      if (localStorage.getItem('SEEN_WISH_LIST_INTRO') === 'true') return false;

      return true;
    };

    const toggleShowAllPrizes = async () => {
      setLoadingMorePrizes(true);
      await sleep(100); // Needed to re-render component and show loading spinner.
      setExpanded(!expanded);
      setLoadingMorePrizes(false);
    };

    // Expands visible prize list once we have nearly scrolled to See More button.
    const seeMoreButton = useRef<HTMLHeadingElement>(null);
    const handleScroll = () => {
      if (window.location.hash?.includes(SUGGEST_PRIZE_ID)) {
        // If we're trying to scroll to the suggest prize area using normal browser URL-fragment scrolling, then we
        // should skip loading more on scroll
        return;
      }
      if (
        !expanded &&
        seeMoreButton.current &&
        seeMoreButton.current?.getBoundingClientRect()?.y -
          (window.innerHeight +
            200) /* so we load slightly before we see the button */ <
          0
      ) {
        toggleShowAllPrizes();
      }
    };

    useEffect(() => {
      window.addEventListener('scroll', handleScroll);

      if (guestDetails?.wonByGuest) {
        setCreateAccountPopupAccountInfo(guestDetails);
      } else if (shouldShowWishListIntroductionModal()) {
        setShowIntroducingWishListModal(true);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    return (
      <MainLayout additionalClassName="prizesPage">
        <NextSeo
          title={metaTitle}
          description={metaDescription}
          openGraph={{
            title: metaTitle,
            description: metaDescription,
          }}
        />

        {createAccountPopupAccountInfo && (
          <CreateAccountPopup
            prompt={Prompt.INTRO_BLOCKING_PRIZE_REDEMPTION}
            userId={createAccountPopupAccountInfo.userId}
            emailAddress={createAccountPopupAccountInfo.emailAddress}
            onClose={() => setCreateAccountPopupAccountInfo(null)}
          />
        )}

        {showIntroducingWishListModal && (
          <IntroducingWishListModal
            onClose={() => {
              localStorage.setItem('SEEN_WISH_LIST_INTRO', 'true');
              setShowIntroducingWishListModal(false);
            }}
          />
        )}

        {selectedPrize && (
          <ChoosePrizeModal
            selectedPrize={selectedPrize}
            closePopup={() => setSelectedPrize(null)}
            tokenRedemptionOptionId={tokenRedemptionOption}
            prizeWinValidationResult={validationResult}
            onSuccessfulChoiceHandler={async () => {
              setCurrentUser(await getMe(getCookie('jwt', null))); // Refetch the current user to refresh the unclaimed prizes badge count in header and on account page.
              setSelectedPrize(null);
              if (tokenRedemptionOption) {
                Router.push('/token-town');
              } else {
                navigateToAccount();
              }
            }}
          />
        )}

        <PageTitle
          heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
          subheadingComponent={
            choosingPrize ? (
              <p style={{ fontSize: '1.1rem', textAlign: 'center' }}>
                Click on a prize to learn more — if you fancy the prize, click
                the orange button to claim it!
              </p>
            ) : null
          }
          style={{
            minHeight: choosingPrize ? 170 : 135,
          }}
          tagClassName={
            !choosingPrize
              ? 'w-full container !text-3xl text-left !font-heavy normal-case max-w-screen-lg lg:px-8'
              : undefined
          }
        >
          <PrizePageNav
            prizeCategoryName={prizePageNavPrizeCategoryName}
            prizeCategoryHref={prizePageNavPrizeCategoryHref}
            className="md:px-4 lg:px-2 mb-10"
          />

          {choosingPrize ? (
            <>
              Choose Your <strong>Prize</strong>
            </>
          ) : (
            <div
              className="md:p-4"
              style={{
                fontSize: '1.1em',
              }}
            >
              Prizes
            </div>
          )}
        </PageTitle>
        {!choosingPrize && <PrizeFilterSelector selectedFilterId={filterId} />}
        {inGroupedRedemptionFlow || prizesToSelect ? (
          <RedemptionFlow
            prizes={prizesToSelect}
            setSelectedPrize={setSelectedPrize}
          />
        ) : (
          <PaddedMaxWidthContainer
            style={{ margin: 0, width: '100%', isolation: 'isolate' }}
          >
            <div className="p-6 mt-8 md:px-10 w-full">
              <div className="w-full md:mb-10">
                {!choosingPrize && (
                  <div className="w-full md:flex">
                    <div className="w-full md:w-2/3 md:pr-4">
                      <Subheading
                        style={{ textTransform: 'unset', textAlign: 'left' }}
                      >
                        {title}
                      </Subheading>
                      <p
                        className="!font-semibold"
                        style={{
                          fontSize: '1.3em',
                          fontFamily: "'Montserrat', sans-serif",
                        }}
                      >
                        {description}
                      </p>
                    </div>
                    {[null, ...LottoPrizeValueFilterOptions].includes(
                      valueFilter
                    ) && (
                      <div className="flex justify-end mt-4 items-end p-1 w-full md:w-1/3 ">
                        <PrizeValueDropdownSelector
                          value={valueFilter}
                          onChange={setValueFilter}
                        />
                      </div>
                    )}
                  </div>
                )}

                {currentUser &&
                  [(SubscriberStatus.FREE, SubscriberStatus.LITE)].includes(
                    getSubscriberStatus(currentUser)
                  ) && (
                    <p
                      style={{
                        fontStyle: 'italic',
                        textAlign: 'left',
                        fontSize: '1.1em',
                        padding: '1em 0',
                        width: '100%',
                      }}
                    >
                      Upgrade to DAYMADE Premium to unlock all of our prizes.
                    </p>
                  )}
              </div>

              <div className="categoryAndFiltersSectionSeparator">
                <Separator style={{ width: '98%' }} />
              </div>

              {!!filterId && !!subcategories && subcategories.length > 0 && (
                <div className="subcategorySelectorContainer">
                  <SubcategorySelector
                    selectedSubcategory={selectedSubcategory}
                    setSelectedSubcategory={setSelectedSubcategory}
                    subcategories={subcategories}
                  />
                </div>
              )}
            </div>

            <PrizeList pageSize={undefined} style={undefined}>
              {filteredPrizes.map((prize: Prize, key: number) => (
                <PrizeCardWithTitleBelow
                  additionalCardClass={'h-64 md:h-80 lg:h-96'}
                  titleStyle={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                  }}
                  subheadingStyle={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 400,
                  }}
                  key={key}
                  prize={prize}
                  withFavouriteButton={true}
                  href={
                    !choosingPrize ? `/prizes/details/${prize.slug}` : undefined
                  }
                  onClick={
                    choosingPrize
                      ? (event) => {
                          event.preventDefault();
                          setSelectedPrize(prize);
                        }
                      : undefined
                  }
                  showLockedPrizeBannerIfApplicable={!choosingPrize}
                  style={undefined}
                  pageSectionForAdminSiteOnly={undefined}
                  onChangePrizePageOrdering={undefined}
                  customImageOpacity={undefined}
                />
              ))}
            </PrizeList>

            {!inGroupedRedemptionFlow && filteredPrizes.length === 0 && (
              <PaddedMaxWidthContainer>
                <img
                  src={`${ASSET_PREFIX}/images/no-prizes.png`}
                  style={{ width: '120px', margin: '1.5em auto' }}
                />

                <p
                  style={{
                    fontSize: '1.2em',
                    margin: '0 1em',
                    textAlign: 'center',
                  }}
                >
                  No prizes currently available in this category. Got any
                  suggestions? Send us an email:{' '}
                  <a href="mailto:hello@daymade.co.uk">hello@daymade.co.uk</a>.
                  <br />
                  <br />
                  In the meantime, choose a different category at the top of the
                  page to find more prizes.
                </p>
              </PaddedMaxWidthContainer>
            )}

            {shouldShowExpandButton && loadingMorePrizes ? (
              <PlainLoadingSpinner style={undefined} />
            ) : (
              shouldShowExpandButton && (
                <ToggleButton
                  chevronReversed={expanded}
                  toggleHandler={toggleShowAllPrizes}
                  style={{
                    flexDirection: expanded ? 'column' : 'column-reverse',
                    margin: '0 auto',
                  }}
                  additionalClassName={undefined}
                >
                  <h4
                    ref={seeMoreButton}
                    style={{ margin: '0.5em', fontSize: '1.3em' }}
                  >
                    {expanded ? 'Show Less' : 'Show All'}
                  </h4>
                </ToggleButton>
              )
            )}
          </PaddedMaxWidthContainer>
        )}

        <p
          style={{
            textAlign: 'center',
            margin: '2em',
            fontStyle: 'italic',
            fontWeight: 600,
            maxWidth: '1000px',
            fontFamily: '"Montserrat", sans-serif',
          }}
        >
          {availabilityNotice}
        </p>

        {choosingPrize && !tokenRedemptionOption ? (
          <TestimonialAdSection />
        ) : (
          !choosingPrize && (
            <SubmitPrizeSection
              prizeCategory="Weekly Draw"
              descriptionText="Have something you'd want the chance to experience? A place you've always wanted to go? Let us know!"
              suggestAPrizeId={SUGGEST_PRIZE_ID}
            />
          )
        )}

        {bottomInfoAndFaqSection && (
          <div style={{ width: '100%', background: colors.footerBackground }}>
            <div
              className="flexCentered bottomInfoSection"
              style={{
                width: 'auto',
                maxWidth: '1000px',
                margin: '4em auto 2em',
                padding: '0 2em',
                alignItems: 'flex-start',
              }}
            >
              {bottomInfoAndFaqSection}
            </div>
          </div>
        )}

        <style jsx>{`
          .prizeTierSelectorContainer {
            grid-column: 2 / 3;
            grid-row: 1 / 2;

            display: flex;
            flex-wrap: wrap;
            flex-grow: 1;
            align-items: center;
            padding-left: 1em;
          }

          .categoryAndFiltersSectionSeparator {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
          }

          .subcategorySelectorContainer {
            grid-column: 1 / 3;
            grid-row: 3 / 4;

            padding-top: 1em;
          }

          @media (max-width: 700px) {
            .categoryAndFiltersSection {
              padding: 2em 1em;
            }

            .subcategorySelectorContainer {
              grid-row: 2 / 3;

              padding-top: 0;
              padding-bottom: 1em;
            }

            .categoryAndFiltersSectionSeparator {
              grid-row: 3 / 4;
            }

            .prizeTierSelectorContainer {
              grid-column: 1 / 3;
              grid-row: 4 / 5;

              display: flex;
              justify-content: center;
              padding-top: 1em;
              padding-left: 0;
            }
          }

          .enjoyLaterBadgeContainer {
            position: absolute;
            width: 100%;
            max-width: 1000px;
            top: 100px;
            text-align: right;
          }

          .enjoyLaterBadge {
            display: inline;
            width: 40%;
            max-width: 192px;
            height: auto;
          }

          :global(.trendingPrizesContainer) {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            grid-gap: 5px;
            padding-bottom: 0.5em;
          }

          :global(.trendingPrizesGrid) {
            width: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 5px;
            padding-bottom: 0.5em;
          }

          @media (min-width: 800px) {
            :global(.trendingPrizesGrid) {
              grid-template-columns: 1fr 1fr 1fr 1fr;
            }
          }

          :global(.footer) {
            margin-top: 0 !important;
          }

          :global(.bottomInfoSection .questionHeading) {
            font-size: 1em;
            letter-spacing: normal;
            text-transform: none;
            margin: 1em 0;
          }
        `}</style>
      </MainLayout>
    );
  }
);

export const getServerSideProps: GetServerSideProps<{
  prizesAreGroupedByPageSection?: boolean;
  tokenRedemptionOption?: string;
  tier?: string;
  pwid?: string;
  emailAddress?: string;
  prizes?: any;
  legacyValue?: string;
}> = async ({ query, res }) => {
  const {
    pwid: prizeWinId,
    emailAddress,
    tokenRedemptionOption,
    showLegacyPrizesForValue,
    tier: tierQueryString,
    value: legacyValue,
  } = query;

  if (tokenRedemptionOption && (tierQueryString || legacyValue)) {
    return {
      props: {
        prizesAreGroupedByPageSection: true,
        tokenRedemptionOption: (tokenRedemptionOption as string) ?? null,
        tier: (tierQueryString as string) ?? null,
        legacyValue: (legacyValue as string) ?? null,
      },
    };
  }

  if (showLegacyPrizesForValue) {
    let prizes = await fetchLegacyPrizesForValue(
      showLegacyPrizesForValue as string
    );
    return {
      props: {
        prizes,
      },
    };
  }
  if (prizeWinId) {
    return {
      props: {
        pwid: prizeWinId as string,
        ...(emailAddress ? { emailAddress: emailAddress as string } : {}),
      },
    };
  }

  res.setHeader(
    "Cache-Control",
    "public, max-age=3600, s-maxage=3600, stale-while-revalidate=59"
  );
  let prizes = await fetchPrizes();
  return {
    props: {
      prizes: prizes,
    },
  };
};

export default PrizesPage;
